import 'isomorphic-fetch';
import ExecutionEnvironment from 'exenv';

import { getRequestGuid, logger } from 'helpers/environment/context';
let now;
if (ExecutionEnvironment.canUseDOM) {
  now = () => window.performance && window.performance.now && window.performance.now() || new Date().getTime();
} else {
  const { performance } = require('perf_hooks');
  ({ now } = performance);
}

function extractGuidFromFetchArgs(args) {
  return args.length === 2 &&
    typeof args[1] === 'object' &&
    args[1].headers &&
    'X-UUID' in args[1].headers &&
    args[1].headers['X-UUID'];
}

/**
 * Fetch factory which constructs a fetch implementation which logs the execution time of the fetch call.
 * @param  {String} [context='']    A string to include in the log message to more easily group logging statements with the call type
 * @param  {[type]} [fetcher=fetch] the fetch function to delegate to
 * @param  {[type]} [log=logDebug]  function to log to
 * @return {[type]}                 a fetch function that can be used over and over again that will fetch using the specified delegate and  log with the given context and logger.
 */
export function timedFetchWithLogContext(context = '', fetcher = fetch, log = logger.debug) {
  return function() {
    const begin = now();
    const retVal = fetcher.apply(this, arguments);
    retVal.then(resp =>{
      const guid = extractGuidFromFetchArgs(arguments);
      // if the fetch request contains a uuid, let's log it when we log the timing.
      const guidLogFragment = guid ? ` api_guid=${guid}` : '';
      const end = now();
      log(`API Call Completed context=${context} time=${(end - begin).toFixed(0)} ms statusCode=${resp.status} url="${arguments[0]}"${guidLogFragment}`);
      return resp;
    });
    return retVal;
  };
}

function modifyFetchArgsWithGuid(args) {
  const uuid = getRequestGuid();
  if (args.length === 2) {
    const [url, originalOptions] = args;
    if (originalOptions.headers && originalOptions.headers['X-UUID']) {
      return args;
    } else {
      return [url, { ...originalOptions, headers: { ...originalOptions.headers, 'X-UUID': uuid } }];
    }
  } else if (args.length === 1 && typeof args[0] === 'string') {
    return [args[0], { headers: { 'X-UUID': uuid } }];

  } else if (args.length === 1 && typeof args[0] === 'object') {
    const [req] = args;
    if (!req.headers.has('X-UUID')) {
      req.headers.append('X-UUID', uuid);
    }
    return [req];

  } else {
    logger.error('Unsupported arguments to fetch.');
    return args;
  }
}

// We cant inject uuid client side because none of the webservices have CORS setup to allow it.
export default ExecutionEnvironment.canUseDOM ? timedFetchWithLogContext : timedFetchWithUUId;

export function timedFetchWithUUId(context = '', timedFetcherFactory = timedFetchWithLogContext) {
  return function() {
    const newArgs = modifyFetchArgsWithGuid(arguments);
    return timedFetcherFactory(context).apply(this, newArgs);
  };
}
